/**
 * ProductQuickViewModal - Product quick view modal component
 *
 * Features:
 * - React Portal rendering to document.body
 * - Image carousel with navigation
 * - Product info display (title, SKU, status, description, price)
 * - Role-based button display (customer/admin modes)
 * - ESC key and backdrop click to close
 * - Body scroll lock when open
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { X, Eye, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Product, ProductListItem } from '../../../types';
import type { ProductQuickViewModalProps } from './types';
import './ProductQuickViewModal.css';

const ProductQuickViewModal = ({
  product,
  mode,
  isOpen,
  onClose,
  onEdit,
  onAddToCart,
  onBuy,
  userRole,
}: ProductQuickViewModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Type guards to safely access optional properties
  const hasDescription = (p: Product | ProductListItem): p is Product => {
    return 'description' in p;
  };

  // Collect all images: featured image + additional images
  const allImages = [
    product.featuredImageUrl,
    ...(product.imageUrls || []),
  ].filter(Boolean) as string[];

  // Handle close with image reset - wrapped in useCallback for stable reference
  const handleClose = useCallback(() => {
    setCurrentImageIndex(0);
    onClose();
  }, [onClose]);

  // Reset image index when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentImageIndex(0);
    }
  }, [isOpen]);

  // Handle body scroll lock and ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.classList.add('quick-view-open');

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('quick-view-open');
    };
  }, [isOpen, handleClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Navigation handlers
  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  if (!product || !isOpen) return null;

  // Determine price display
  const displayPrice = product.salePrice || product.regularPrice;
  const hasSalePrice = product.salePrice !== null &&
                       product.regularPrice !== null &&
                       product.salePrice < product.regularPrice;

  // Determine which buttons to show based on mode and user role
  const showEditButton = mode === 'admin' || userRole === 'admin' || userRole === 'super_admin';
  const showCustomerButtons = mode === 'customer';

  const modalContent = (
    <div
      className={`product-quick-view-overlay ${isOpen ? 'product-quick-view-overlay--open' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-view-title"
    >
      <div
        className={`product-quick-view-modal ${isOpen ? 'product-quick-view-modal--open' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="product-quick-view__header">
          <h2 id="quick-view-title" className="product-quick-view__header-title">
            <Eye size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Quick View
          </h2>
          <button
            className="product-quick-view__close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="product-quick-view__body">
          {/* Image Carousel */}
          {allImages.length > 0 && (
            <div className="product-quick-view__images">
              <img
                src={allImages[currentImageIndex]}
                alt={`${product.title} - Image ${currentImageIndex + 1}`}
                className="product-quick-view__image"
              />
              {allImages.length > 1 && (
                <div className="product-quick-view__image-nav">
                  <button
                    className="product-quick-view__nav-btn"
                    onClick={handlePreviousImage}
                    aria-label="Previous image"
                    type="button"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="product-quick-view__image-counter">
                    {currentImageIndex + 1} / {allImages.length}
                  </span>
                  <button
                    className="product-quick-view__nav-btn"
                    onClick={handleNextImage}
                    aria-label="Next image"
                    type="button"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Product Info */}
          <div className="product-quick-view__info">
            <h3 className="product-quick-view__title">{product.title}</h3>

            {/* Metadata */}
            <div className="product-quick-view__meta">
              {product.sku && (
                <span className="product-quick-view__sku">SKU: {product.sku}</span>
              )}
              <span
                className={`product-quick-view__status product-quick-view__status--${product.status}`}
              >
                {product.status === 'publish' && '✅ Published'}
                {product.status === 'draft' && 'Draft'}
                {product.status === 'pending' && '⏳ Pending'}
                {product.status === 'private' && '🔒 Private'}
              </span>
            </div>

            {/* Description */}
            {hasDescription(product) && product.description && (
              <div className="product-quick-view__description">
                <ReactMarkdown>{product.description}</ReactMarkdown>
              </div>
            )}

            {/* Price */}
            {displayPrice !== null && (
              <div className="product-quick-view__price">
                {hasSalePrice && product.regularPrice && (
                  <span className="product-quick-view__regular-price">
                    ${product.regularPrice.toFixed(2)}
                  </span>
                )}
                <span className="product-quick-view__sale-price">
                  ${displayPrice.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="product-quick-view__actions">
            {showCustomerButtons && (
              <>
                {onAddToCart && (
                  <button
                    className="product-quick-view__btn product-quick-view__btn--secondary"
                    onClick={() => onAddToCart(product)}
                    type="button"
                  >
                    <ShoppingCart size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                    Add to Cart
                  </button>
                )}
                {onBuy && (
                  <button
                    className="product-quick-view__btn product-quick-view__btn--primary"
                    onClick={() => onBuy(product)}
                    type="button"
                  >
                    Buy
                  </button>
                )}
              </>
            )}

            {showEditButton && onEdit && (
              <button
                className="product-quick-view__btn product-quick-view__btn--primary"
                onClick={() => onEdit(product)}
                type="button"
              >
                Edit
              </button>
            )}

            <button
              className="product-quick-view__btn product-quick-view__btn--close"
              onClick={handleClose}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using Portal to body
  return createPortal(modalContent, document.body);
};

export default ProductQuickViewModal;
